import { FC, useState, MouseEvent, useEffect } from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import MainLayout from 'src/pages/private';
import { useFormik } from 'formik';
import { grnInitialValues } from 'src/utils/initialValues.utils';
import {
  createEmptyGRNItem,
  getDefaultGRNItems,
} from 'src/utils/initialValues.utils';
import {
  grnFilterValidation,
  grnValidation,
} from 'src/utils/validationScheme.utils';
import StyledSnackbar from 'src/components/StyledComponents/StyledSnackBar';
import { apiUrls, errorMessages, routeURL } from 'src/utils/properties.utils';
import { calculateDialogHeightByItemCount } from 'src/utils/dialog.utils';
import { axiosTokenInstance } from 'src/utils/axiosInstance.utils';
import {
  formatNumberWithCommas,
  handleNumberFieldChange,
} from 'src/utils/format.utils';
import SearchBar from 'src/components/InputFields/Searchbar';
import GRNGrid from 'src/components/Grids/GRNGrid';
import FilterPopover from 'src/components/StyledComponents/FilterPopover';
import GRNStatusPicker from 'src/components/InputFields/GRNStatusPicker';
import CreatedDatePicker from 'src/components/InputFields/DatePicker';
import TextField from 'src/components/InputFields/TextField';
import TextArea from 'src/components/InputFields/TextArea';
import RawMaterialSelect from 'src/components/InputFields/RawMaterialPicker';
import GRNDialog from 'src/components/DialogBox/GRNBox/GRNBox';
import { getApiErrorMessage } from 'src/utils/errorHandler';
import {
  PrimaryActionButton,
  ButtonIcon,
  FilterButton,
  FilterIcon,
  FilterPopoverIcon,
  FilterPopoverTitle,
  FilterPopoverDivider,
  FilterCloseIcon,
  FilterLabel,
  SmallActionButton,
  AddItemIcon,
  AddItemText,
  SpacedContainer,
  ClickableContainer,
  OverflowContainer,
  ErrorHelperTextContainer,
} from 'src/components/StyledComponents/StyledPageComponents';
import {
  FlexRowStartBox,
  FlexColumnBox,
  BorderedSectionBox,
  SectionTitleTypography,
  ItemRowBox,
  ItemFieldBox,
  ItemFieldAlignedBox,
  ItemFieldAlignedBoxMl2,
  RemoveItemBox,
  TrashIcon,
} from 'src/components/DialogBox/POBox/POBox.styles';
import {
  GRNFormItem,
  GRNFormValues,
  GRNViewData,
} from 'src/models/Management.interface';
import VendorSelect from 'src/components/InputFields/VendorPicker';
import VendorAutocomplete from 'src/components/InputFields/VendorAutocomplete';
import { useGetVendor } from 'src/services/vendor.service';
import { useGetBasicMaterials } from 'src/services/material.service';
import ViewGRNDialog from 'src/components/DialogBox/ViewGRNDialog/ViewGRNDialog';

const GRNManagement: FC = () => {
  const theme = useTheme();
  const smScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [filterOpen, setFilterOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [grnAdded, setGrnAdded] = useState(false);
  const { showSnackbar } = StyledSnackbar();
  const [searchTerm, setSearchTerm] = useState('');
  const [items, setItems] = useState(getDefaultGRNItems());
  const [filterValues, setFilterValues] = useState({
    vendorId: '',
    status: '',
    startDate: null as Date | null,
    endDate: null as Date | null,
  });
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [viewData, setViewData] = useState<GRNViewData | null>(null);
  const [selectedGrnId, setSelectedGrnId] = useState<string | null>(null);
  const { basicVendors, fetchBasicVendors } = useGetVendor();
  const { basicMaterials, fetchBasicMaterials } = useGetBasicMaterials();

  // Handle view parameter from URL to open ViewGRNDialog
  useEffect(() => {
    const viewGrnId = searchParams.get('view');
    if (viewGrnId) {
      setSelectedGrnId(viewGrnId);
      handleFetchAndViewGRN(viewGrnId);
    }
  }, [searchParams]);

  const handleFetchAndViewGRN = async (grnId: string) => {
    try {
      const response = await axiosTokenInstance.get(apiUrls.GET_GRN_BY_ID, {
        params: { id: grnId },
      });
      const grnData = response.data.data;

      setViewData({
        id: grnData.id,
        vendorName:
          grnData.vendorName || grnData.vendor?.organizationName || '-',
        createdAt: grnData.createdAt,
        preparedBy: grnData.preparedBy || '-',
        invoiceNumber: grnData.invoiceNumber || '-',
        invoiceAmount: grnData.invoiceAmount || 0,
        notes: grnData.notes,
        status: Number(grnData.status),
        approvedDate: grnData.approvedDate,
        approvedBy: grnData.approvedBy,
        rejectedDate: grnData.rejectedDate,
        rejectedBy: grnData.rejectedBy,
        rejectedNotes: grnData.rejectionNote,
        totalPaidAmount: grnData.totalPaidAmount,
        items: grnData.items || [],
      });
      setViewDialogOpen(true);
    } catch (error) {
      const errorMessage = await getApiErrorMessage(error);
      showSnackbar(errorMessage, 'error');
      // Remove the view parameter if GRN fetch fails
      searchParams.delete('view');
      setSearchParams(searchParams, { replace: true });
    }
  };

  const handleViewDialogClose = () => {
    setViewDialogOpen(false);
    setViewData(null);
    searchParams.delete('view');
    setSearchParams(searchParams, { replace: true });
  };

  const handleSearchChange = (newQuery: string) => {
    setSearchTerm(newQuery);
  };

  const handleClickOpen = () => {
    fetchBasicVendors();
    fetchBasicMaterials();
    setOpen(true);
  };

  const handleClose = () => {
    formik.resetForm();
    setItems(getDefaultGRNItems());
    setOpen(false);
  };

  const handleFilterClick = (event: MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
    setFilterOpen(true);
    filterFormik.setValues({
      vendorPicker: filterValues.vendorId,
      statusPicker: filterValues.status,
      startDate: filterValues.startDate,
      endDate: filterValues.endDate,
    });
  };

  const handleClearAndClose = () => {
    filterFormik.resetForm();
    setFilterValues({
      vendorId: '',
      status: '',
      startDate: null,
      endDate: null,
    });
    setFilterOpen(false);
  };

  const handleFilterClose = () => {
    setAnchorEl(null);
    setFilterOpen(false);
  };

  const handleSubmit = async (values: GRNFormValues, isCompleted: boolean) => {
    setLoading(true);

    const grnItems = items
      .map((item) => {
        const rawMaterialId = values[`rawMaterialId-${item.id}`];
        const receivedQty = values[`receivedQty-${item.id}`];
        const returnedQty = values[`returnedQty-${item.id}`] || 0;
        const unitPrice = values[`unitPrice-${item.id}`];

        return { rawMaterialId, receivedQty, returnedQty, unitPrice };
      })
      .filter((item) => item.rawMaterialId && Number(item.receivedQty) > 0);

    if (grnItems.length === 0) {
      showSnackbar(errorMessages.PLEASE_ADD_AT_LEAST_ONE_VALID_ITEM, 'error');
      setLoading(false);
      return;
    }

    try {
      const response = await axiosTokenInstance.post(apiUrls.ADD_GRN, {
        vendorId: values.vendor,
        invoiceNumber: values.invoiceNumber,
        invoiceAmount: parseFloat(values.invoiceAmount),
        notes: values.notes || null,
        items: grnItems,
        isCompleted: isCompleted,
      });
      showSnackbar(response.data.message, 'success');
      setGrnAdded(!grnAdded);
      handleClose();
    } catch (e) {
      const errorMessage = await getApiErrorMessage(e);
      showSnackbar(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const validateFormItems = async () => {
    const values = formik.values as any;
    const grnItems: GRNFormItem[] = items.map((item) => {
      const rawMaterialId = values[`rawMaterialId-${item.id}`];
      const receivedQty = values[`receivedQty-${item.id}`];
      const returnedQty = values[`returnedQty-${item.id}`] || 0;
      const unitPrice = values[`unitPrice-${item.id}`];

      return {
        id: item.id,
        rawMaterialId,
        receivedQty,
        returnedQty,
        unitPrice,
      };
    });
    setItems(grnItems);
    const errors = await formik.validateForm();
    if (Object.keys(errors).length) {
      const touched: Record<string, boolean> = {
        vendor: true,
        invoiceNumber: true,
        invoiceAmount: true,
      };
      items.forEach((item) => {
        touched[`rawMaterialId-${item.id}`] = true;
        touched[`receivedQty-${item.id}`] = true;
        touched[`returnedQty-${item.id}`] = true;
        touched[`unitPrice-${item.id}`] = true;
      });
      formik.setTouched(touched);
    }
    return Object.keys(errors).length === 0;
  };

  const handleSave = async () => {
    const isFormValid = await validateFormItems();
    if (isFormValid) {
      handleSubmit(formik.values, false);
    }
  };
  const handleSaveAndComplete = async () => {
    const isFormValid = await validateFormItems();
    if (isFormValid) {
      handleSubmit(formik.values, true);
    }
  };

  const handleAddItem = () => {
    setItems([...items, createEmptyGRNItem()]);
  };

  const handleRemoveItem = (id: string) => {
    setItems((prevItems) => prevItems.filter((item) => item.id !== id));
  };

  const handleCheckPayments = () => {
    navigate(routeURL.GRN_PAYMENTS_REDIRECT(selectedGrnId || ''));
  };

  const formik = useFormik({
    initialValues: grnInitialValues,
    validationSchema: grnValidation(items),
    onSubmit: () => {
      // Form submission handled by individual action handlers
    },
  });

  const filterFormik = useFormik({
    initialValues: {
      vendorPicker: '',
      statusPicker: '',
      startDate: null as Date | null,
      endDate: null as Date | null,
    },
    validationSchema: grnFilterValidation,
    onSubmit: (values) => {
      setFilterValues({
        vendorId: values.vendorPicker,
        status: values.statusPicker,
        startDate: values.startDate,
        endDate: values.endDate,
      });
      handleFilterClose();
    },
  });

  const selectedMaterialIds = items
    .map((item) => (formik.values as any)[`rawMaterialId-${item.id}`])
    .filter(Boolean);

  return (
    <MainLayout
      title="GRN Management"
      searchBar={
        <SearchBar
          id="search"
          value={searchTerm}
          onChange={handleSearchChange}
          placeholder="Search by invoice number or vendor"
          maxWidth="290px"
          mt={1}
        />
      }
      button={
        <PrimaryActionButton
          className="btn-primary"
          width="150px"
          onClick={handleClickOpen}
        >
          <ButtonIcon component="i" className="bx bx-plus 1" />
          New GRN
        </PrimaryActionButton>
      }
      filterButton={
        <FilterButton
          className="btn-filter"
          width="100px"
          onClick={handleFilterClick}
        >
          <FilterIcon component="i" className="bx bx-filter" />
          Add Filter
        </FilterButton>
      }
    >
      <FilterPopover
        open={filterOpen && anchorEl !== null}
        anchorEl={anchorEl}
        onClose={handleFilterClose}
      >
        <Box>
          <Box display="flex" alignItems="center">
            <FilterPopoverIcon component="i" className="bx bx-filter" />
            <FilterPopoverTitle>Filter Data By</FilterPopoverTitle>
          </Box>
          <FilterPopoverDivider />
          <FilterCloseIcon
            component="i"
            className="bx bx-x-circle"
            onClick={handleFilterClose}
          />
        </Box>
        <Box display="flex" justifyContent="space-between" mb={1}>
          <VendorSelect
            id="vendorPicker"
            label="Vendor"
            height="28px"
            width="150px"
            customMessage="Vendor"
            textColor="var(--subtext)"
            borderColor="var(--hover-white)"
            fontSize="10px"
            fontWeight="400"
            showLabel={false}
            formikProps={filterFormik}
            useBasicVendors={true}
          />
          <GRNStatusPicker
            id="statusPicker"
            label="Status"
            showLabel={false}
            formikProps={filterFormik}
            customMessage="Status"
            textColor="var(--subtext)"
            borderColor="var(--hover-white)"
            fontSize="10px"
            fontWeight="400"
            height="28px"
            width="150px"
          />
        </Box>
        <Box>
          <FilterLabel>Created at:</FilterLabel>
          <SpacedContainer display="flex" justifyContent="space-between">
            <CreatedDatePicker
              name="startDate"
              placeholder="Start Date"
              formikProps={filterFormik}
              fontSize="10px"
            />
            <CreatedDatePicker
              name="endDate"
              placeholder="End Date"
              formikProps={filterFormik}
              fontSize="10px"
            />
          </SpacedContainer>
          {filterFormik.touched.endDate && filterFormik.errors.endDate && (
            <ErrorHelperTextContainer>
              {filterFormik.errors.endDate}
            </ErrorHelperTextContainer>
          )}
        </Box>
        <Box mt={4} display="flex" justifyContent="space-between">
          <SmallActionButton
            btnVariant="inactive"
            width="70px"
            mr={2}
            onClick={handleClearAndClose}
          >
            Clear
          </SmallActionButton>
          <SmallActionButton
            className="btn-primary"
            width="70px"
            onClick={() => filterFormik.handleSubmit()}
          >
            Apply
          </SmallActionButton>
        </Box>
      </FilterPopover>
      <GRNGrid
        state={grnAdded}
        searchTerm={searchTerm}
        filters={filterValues}
      />
      <Box display="flex">
        <OverflowContainer display="flex" flexDirection="column">
          <GRNDialog
            height={`${calculateDialogHeightByItemCount(items.length)}px`}
            open={open}
            handleClose={handleClose}
            title="Add New GRN"
            subtitle="Fill in the details below to add a new GRN."
            formTitle=""
            onSubmit={formik.handleSubmit}
            smScreen={smScreen}
            buttonDisabled={loading}
            buttonLabel2="Save and Complete"
            onSaveClick={handleSave}
            onSaveAndCompleteClick={handleSaveAndComplete}
          >
            <Box display="flex" flexDirection="column">
              <FlexRowStartBox>
                <FlexColumnBox>
                  <VendorAutocomplete
                    id="vendor"
                    label="Vendor"
                    formikProps={formik}
                    options={basicVendors}
                    width="238px"
                  />
                </FlexColumnBox>
                <FlexColumnBox>
                  <TextField
                    color="var(--subtext)"
                    width="238px"
                    id="invoiceNumber"
                    label="Invoice Number"
                    formikProps={formik}
                  />
                </FlexColumnBox>
                <FlexColumnBox>
                  <TextField
                    color="var(--subtext)"
                    width="238px"
                    id="invoiceAmount"
                    label="Invoice Amount"
                    formikProps={formik}
                    type="text"
                    value={formatNumberWithCommas(formik.values.invoiceAmount)}
                    onChange={(e) =>
                      handleNumberFieldChange(e, formik, 'invoiceAmount')
                    }
                  />
                </FlexColumnBox>
              </FlexRowStartBox>
              <Box mb={3}>
                <TextArea
                  color="var(--subtext)"
                  id="notes"
                  label="Notes"
                  formikProps={formik}
                  rows={3}
                  height="100%"
                  width="578px"
                  optional={true}
                  overflowY="hidden"
                />
              </Box>
              <Box>
                <SectionTitleTypography>Items</SectionTitleTypography>
                <BorderedSectionBox>
                  {items.map((item, index) => (
                    <ItemRowBox key={item.id}>
                      <ItemFieldBox>
                        <RawMaterialSelect
                          showLabel={index === 0}
                          color="var(--subtext)"
                          width="235px"
                          id={`rawMaterialId-${item.id}`}
                          label="Item"
                          formikProps={formik}
                          selectedIds={selectedMaterialIds}
                          rawMaterialsList={basicMaterials}
                        />
                      </ItemFieldBox>
                      <ItemFieldAlignedBox>
                        <TextField
                          showLabel={index === 0}
                          color="var(--subtext)"
                          width="160px"
                          id={`receivedQty-${item.id}`}
                          label="Received Quantity"
                          formikProps={formik}
                          type="text"
                          value={formatNumberWithCommas(
                            (formik.values as any)[`receivedQty-${item.id}`]
                          )}
                          onChange={(e) =>
                            handleNumberFieldChange(
                              e,
                              formik,
                              `receivedQty-${item.id}`
                            )
                          }
                        />
                      </ItemFieldAlignedBox>
                      <ItemFieldAlignedBoxMl2>
                        <TextField
                          showLabel={index === 0}
                          color="var(--subtext)"
                          width="160px"
                          id={`returnedQty-${item.id}`}
                          label="Returned Quantity"
                          formikProps={formik}
                          type="text"
                          value={formatNumberWithCommas(
                            (formik.values as any)[`returnedQty-${item.id}`]
                          )}
                          onChange={(e) =>
                            handleNumberFieldChange(
                              e,
                              formik,
                              `returnedQty-${item.id}`
                            )
                          }
                        />
                      </ItemFieldAlignedBoxMl2>
                      <ItemFieldAlignedBoxMl2>
                        <TextField
                          showLabel={index === 0}
                          color="var(--subtext)"
                          width="160px"
                          id={`unitPrice-${item.id}`}
                          label="Unit Price"
                          formikProps={formik}
                          type="text"
                          value={formatNumberWithCommas(
                            (formik.values as any)[`unitPrice-${item.id}`]
                          )}
                          onChange={(e) =>
                            handleNumberFieldChange(
                              e,
                              formik,
                              `unitPrice-${item.id}`
                            )
                          }
                        />
                      </ItemFieldAlignedBoxMl2>
                      {items.length > 1 && (
                        <RemoveItemBox>
                          <TrashIcon
                            component="i"
                            className="bx bx-trash"
                            onClick={() => handleRemoveItem(item.id)}
                          />
                        </RemoveItemBox>
                      )}
                    </ItemRowBox>
                  ))}
                </BorderedSectionBox>
              </Box>
              <ClickableContainer
                width="100px"
                display="flex"
                mt={2}
                mb={2}
                onClick={handleAddItem}
              >
                <AddItemIcon component="i" className="bx bx-plus" />
                <AddItemText>Add Item</AddItemText>
              </ClickableContainer>
            </Box>
          </GRNDialog>
        </OverflowContainer>
      </Box>
      <ViewGRNDialog
        open={viewDialogOpen}
        onClose={handleViewDialogClose}
        data={viewData}
        onCheckPayments={handleCheckPayments}
      />
    </MainLayout>
  );
};

export default GRNManagement;
